import moment from 'moment';

import { EditInternalGroupData } from '../../../typings/groupsEditState';
import {
    AdditionalTargetingField,
    NumericTargetingKey,
    SuggestTargetingKey,
    SuggestWithStringDictTargetingKey,
    SuggestWithVersionTargetingKey,
    TextTargetingKey
} from '../../../typings/internalGroups';
import { State } from '../../../typings/state';
import { ErrorOrWarning } from '../../ErrorHelpers/ErrorHelpers';
import { fetchInterfaceLangs, getStringDictNameByTargetingKey } from '../InternalGroupHelpers';

export enum InternalGroupDefectCodes {
    MUST_BE_GREATER_THAN_OR_EQUAL_TO_MIN = 'NumberDefectIds.MUST_BE_GREATER_THAN_OR_EQUAL_TO_MIN',
    MUST_BE_LESS_THEN_OR_EQUAL_TO_MAX = 'NumberDefectIds.MUST_BE_LESS_THEN_OR_EQUAL_TO_MAX',
    CANNOT_BE_EMPTY = 'CollectionDefectIds.Gen.CANNOT_BE_EMPTY',
    FINISH_TIME_SHOULD_BE_GREATER_THAN_START_TIME = 'AdGroupDefectIds.Gen.FINISH_TIME_SHOULD_BE_GREATER_THAN_START_TIME',
    INVALID_VALUE = 'DefectIds.INVALID_VALUE',
    VERSIONED_TARGETING_WITH_MULTIPLE_VERSIONS = 'AdGroupAdditionalTargetingsDefectIds.Gen.VERSIONED_TARGETING_WITH_MULTIPLE_VERSIONS',
    INCORRECT_VERSION = 'AdGroupAdditionalTargetingsDefectIds.Gen.INCORRECT_VERSION',
    MUST_BE_GREATER_THAN_MIN = 'NumberDefectIds.MUST_BE_GREATER_THAN_MIN',
    EMPTY_REGIONS = 'RegionIdDefectIds.Gen.EMPTY_REGIONS'
}

const TARGETING_VERSION_REG_EXP = /^[1-9][0-9]{0,2}([.][0-9]{1,3})?$/;
const YANDEX_UID_REG_EXP = /^[1-9][0-9]{16,18}$|^%[0-9]{1,2}$/;

const MIN_LEVEL_VALUE = 0;
const MAX_LEVEL_VALUE = 1000;

const errorPath = (field: string, index: number, isNew: boolean, subField?: string): string =>
    isNew
        ? `addItems[${index}].${field}${subField ? `.${subField}` : ''}`
        : `updateItems[${index}].${field}${subField ? `.${subField}` : ''}`;

export class InternalGroupValidator {
    private static textTargetingFields: TextTargetingKey[] = [
        AdditionalTargetingField.YS_COOKIES,
        AdditionalTargetingField.QUERY_OPTIONS,
        AdditionalTargetingField.QUERY_REFERERS,
        AdditionalTargetingField.USER_AGENTS,
        AdditionalTargetingField.DEVICE_NAMES
    ];

    private static numericTargetingFields: NumericTargetingKey[] = [
        AdditionalTargetingField.TEST_IDS,
        AdditionalTargetingField.CLIDS,
        AdditionalTargetingField.CLID_TYPES
    ];

    private static suggestTargetingFields: SuggestTargetingKey[] = [
        AdditionalTargetingField.DESKTOP_INSTALLED_APPS,
        AdditionalTargetingField.INTERFACE_LANGS
    ];

    private static suggestWithUaTraitsTargetingFields: SuggestWithStringDictTargetingKey[] = [
        AdditionalTargetingField.DEVICE_VENDORS,
        AdditionalTargetingField.OS_NAMES
    ];

    private static suggestWithVersionTargetingFields: SuggestWithVersionTargetingKey[] = [
        AdditionalTargetingField.BROWSER_ENGINES,
        AdditionalTargetingField.BROWSER_NAMES,
        AdditionalTargetingField.OS_FAMILIES
    ];

    public static validateGroup(group: EditInternalGroupData, state: State, index: number = 0): ErrorOrWarning[] {
        return [
            ...InternalGroupValidator.validateTargetingShowDates(group, index),
            ...InternalGroupValidator.validateYandexUids(group, index),
            ...InternalGroupValidator.textTargetingFields.reduce(
                (acc, field) => [
                    ...acc,
                    ...InternalGroupValidator.validateTextAdditionalTargeting(group, index, field)
                ],
                []
            ),
            ...InternalGroupValidator.numericTargetingFields.reduce(
                (acc, field) => [
                    ...acc,
                    ...InternalGroupValidator.validateNumericAdditionalTargeting(group, index, field)
                ],
                []
            ),
            ...InternalGroupValidator.suggestTargetingFields.reduce(
                (acc, field) => [
                    ...acc,
                    ...InternalGroupValidator.validateSuggestAdditionalTargeting(group, index, field, state)
                ],
                []
            ),
            ...InternalGroupValidator.suggestWithUaTraitsTargetingFields.reduce(
                (acc, field) => [
                    ...acc,
                    ...InternalGroupValidator.validateSuggestWithUaTraitsAdditionalTargeting(group, index, field, state)
                ],
                []
            ),
            ...InternalGroupValidator.suggestWithVersionTargetingFields.reduce(
                (acc, field) => [
                    ...acc,
                    ...InternalGroupValidator.validateSuggestWithVersionAdditionalTargeting(group, index, field, state)
                ],
                []
            ),
            ...InternalGroupValidator.validateStartTimeFinishTime(group, index),
            ...InternalGroupValidator.validateLevel(group, index),
            ...InternalGroupValidator.validateRegions(group, index)
        ];
    }

    private static validateTextAdditionalTargeting(
        group: EditInternalGroupData,
        index: number = 0,
        key: TextTargetingKey
    ): ErrorOrWarning[] {
        const { isNew, value } = group;
        const targetingValue = value[key];
        const validationResult: ErrorOrWarning[] = [];

        if (!targetingValue) {
            return validationResult;
        }

        const targetingValueItems = targetingValue.value;

        if (!targetingValueItems || targetingValueItems.length === 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });

            return validationResult;
        }

        const hasEmptyValues = targetingValueItems.some(item => item.trim() === '');

        if (hasEmptyValues) {
            validationResult.push({
                code: InternalGroupDefectCodes.INVALID_VALUE,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        return validationResult;
    }

    private static validateNumericAdditionalTargeting(
        group: EditInternalGroupData,
        index: number = 0,
        key: NumericTargetingKey
    ): ErrorOrWarning[] {
        const { isNew, value } = group;
        const targetingValue = value[key];
        const validationResult: ErrorOrWarning[] = [];

        if (!targetingValue) {
            return validationResult;
        }

        const targetingValueItems = targetingValue.value;

        if (!targetingValueItems || targetingValueItems.length === 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });

            return validationResult;
        }

        const hasNonNumericTags = targetingValueItems.some(
            item => String(item).trim() === '' || isNaN(Number(item)) || Number(item) > Number.MAX_SAFE_INTEGER
        );

        if (hasNonNumericTags) {
            validationResult.push({
                code: InternalGroupDefectCodes.INVALID_VALUE,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        const hasNegativeTags = targetingValueItems.some(item => Number(item) < 0);

        if (hasNegativeTags) {
            validationResult.push({
                code: InternalGroupDefectCodes.MUST_BE_GREATER_THAN_MIN,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        return validationResult;
    }

    private static validateYandexUids(group: EditInternalGroupData, index: number = 0): ErrorOrWarning[] {
        const {
            isNew,
            value: { targetingYandexUids }
        } = group;
        const validationResult: ErrorOrWarning[] = [];

        if (!targetingYandexUids) {
            return validationResult;
        }

        const { value } = targetingYandexUids;

        if (!value || value.length === 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                params: null,
                path: errorPath(AdditionalTargetingField.YANDEX_UIDS, index, isNew, 'value')
            });

            return validationResult;
        }

        const hasInvalidItems = value.some(item => !YANDEX_UID_REG_EXP.test(item));

        if (hasInvalidItems) {
            validationResult.push({
                code: InternalGroupDefectCodes.INVALID_VALUE,
                params: null,
                path: errorPath(AdditionalTargetingField.YANDEX_UIDS, index, isNew, 'value')
            });
        }

        return validationResult;
    }

    private static validateRegions(group: EditInternalGroupData, index: number = 0): ErrorOrWarning[] {
        const {
            isNew,
            value: { regions }
        } = group;

        if (!regions || !regions.regionIds || regions.regionIds.length === 0) {
            return [
                {
                    code: InternalGroupDefectCodes.EMPTY_REGIONS,
                    params: null,
                    path: errorPath('regionIds', index, isNew, 'value')
                }
            ];
        }

        return [];
    }

    private static validateSuggestWithUaTraitsAdditionalTargeting(
        group: EditInternalGroupData,
        index: number = 0,
        key: SuggestWithStringDictTargetingKey,
        state: State
    ): ErrorOrWarning[] {
        const {
            commonData: { stringDicts }
        } = state;
        const { isNew, value } = group;
        const targetingValue = value[key];
        const validationResult: ErrorOrWarning[] = [];

        if (!targetingValue || !stringDicts) {
            return validationResult;
        }

        const targetingValueItems = targetingValue.value;

        if (!targetingValueItems || targetingValueItems.length === 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });

            return validationResult;
        }

        const stringDictName = getStringDictNameByTargetingKey(key);

        const dictionary = stringDictName && stringDicts ? stringDicts[stringDictName] || [] : [];

        const hasInvalidTags = targetingValueItems.some(
            item => !dictionary.find(dictionaryItem => dictionaryItem.val === item.targetingValueEntryId)
        );

        if (hasInvalidTags) {
            validationResult.push({
                code: InternalGroupDefectCodes.INVALID_VALUE,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        return validationResult;
    }

    private static validateSuggestAdditionalTargeting(
        group: EditInternalGroupData,
        index: number = 0,
        key: SuggestTargetingKey,
        state: State
    ): ErrorOrWarning[] {
        const {
            commonData: { stringDicts }
        } = state;
        const { isNew, value } = group;
        const targetingValue = value[key];
        const validationResult: ErrorOrWarning[] = [];

        if (!targetingValue || !stringDicts) {
            return validationResult;
        }

        const targetingValueItems = targetingValue.value;

        if (!targetingValueItems || targetingValueItems.length === 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });

            return validationResult;
        }

        const stringDictName = getStringDictNameByTargetingKey(key);

        const dictionary = stringDictName && stringDicts ? stringDicts[stringDictName] || [] : fetchInterfaceLangs();

        const hasInvalidTags = targetingValueItems.some(
            item => !dictionary.find(dictionaryItem => dictionaryItem.val === item)
        );

        if (hasInvalidTags) {
            validationResult.push({
                code: InternalGroupDefectCodes.INVALID_VALUE,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        return validationResult;
    }

    private static validateSuggestWithVersionAdditionalTargeting(
        group: EditInternalGroupData,
        index: number = 0,
        key: SuggestWithVersionTargetingKey,
        state: State
    ): ErrorOrWarning[] {
        const {
            commonData: { stringDicts }
        } = state;
        const { isNew, value } = group;
        const targetingValue = value[key];
        const validationResult: ErrorOrWarning[] = [];

        if (!targetingValue || !stringDicts) {
            return validationResult;
        }

        const targetingValueItems = targetingValue.value;

        if (!targetingValueItems || targetingValueItems.length === 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });

            return validationResult;
        }

        const stringDictName = getStringDictNameByTargetingKey(key);

        const dictionary = stringDictName && stringDicts ? stringDicts[stringDictName] || [] : [];

        const hasInvalidTargetings = targetingValueItems.some(
            item => !dictionary.find(dictionaryItem => dictionaryItem.val === item.targetingValueEntryId)
        );

        if (hasInvalidTargetings) {
            validationResult.push({
                code: InternalGroupDefectCodes.INVALID_VALUE,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        const hasMoreThanOneVersionedTargeting =
            targetingValueItems.filter(item => item.minVersion || item.maxVersion).length > 1;

        if (hasMoreThanOneVersionedTargeting) {
            validationResult.push({
                code: InternalGroupDefectCodes.VERSIONED_TARGETING_WITH_MULTIPLE_VERSIONS,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        const hasInvalidVersions = targetingValueItems.some(item => {
            const { maxVersion, minVersion } = item;
            if (!maxVersion && !minVersion) {
                return false;
            }

            if (!minVersion && maxVersion) {
                return !TARGETING_VERSION_REG_EXP.test(maxVersion);
            }

            if (!maxVersion && minVersion) {
                return !TARGETING_VERSION_REG_EXP.test(minVersion);
            }

            if (minVersion && maxVersion) {
                return (
                    !TARGETING_VERSION_REG_EXP.test(minVersion) ||
                    !TARGETING_VERSION_REG_EXP.test(maxVersion) ||
                    Number(minVersion) > Number(maxVersion)
                );
            }

            return false;
        });

        if (hasInvalidVersions) {
            validationResult.push({
                code: InternalGroupDefectCodes.INCORRECT_VERSION,
                params: null,
                path: errorPath(key, index, isNew, 'value')
            });
        }

        return validationResult;
    }

    private static validateLevel(group: EditInternalGroupData, index: number): ErrorOrWarning[] {
        const {
            isNew,
            value: { level }
        } = group;

        if (level === undefined) {
            return [
                {
                    code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                    params: null,
                    path: errorPath('level', index, isNew)
                }
            ];
        }

        if (Number(level) < MIN_LEVEL_VALUE) {
            return [
                {
                    code: InternalGroupDefectCodes.MUST_BE_GREATER_THAN_OR_EQUAL_TO_MIN,
                    params: null,
                    path: errorPath('level', index, isNew)
                }
            ];
        }

        if (Number(level) > MAX_LEVEL_VALUE) {
            return [
                {
                    code: InternalGroupDefectCodes.MUST_BE_LESS_THEN_OR_EQUAL_TO_MAX,
                    params: null,
                    path: errorPath('level', index, isNew)
                }
            ];
        }

        return [];
    }

    private static validateStartTimeFinishTime(group: EditInternalGroupData, index: number = 0): ErrorOrWarning[] {
        const validationResult: ErrorOrWarning[] = [];
        const {
            isNew,
            value: { startTime, finishTime }
        } = group;

        if (finishTime && moment(startTime).diff(moment(finishTime)) >= 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.FINISH_TIME_SHOULD_BE_GREATER_THAN_START_TIME,
                params: null,
                path: errorPath('finishTime', index, isNew)
            });
        }

        return validationResult;
    }

    private static validateTargetingShowDates(group: EditInternalGroupData, index: number = 0): ErrorOrWarning[] {
        const {
            isNew,
            value: { targetingShowDates }
        } = group;
        const validationResult: ErrorOrWarning[] = [];

        if (!targetingShowDates) {
            return validationResult;
        }

        const { value } = targetingShowDates;

        if (!value || value.length === 0) {
            validationResult.push({
                code: InternalGroupDefectCodes.CANNOT_BE_EMPTY,
                params: null,
                path: errorPath(AdditionalTargetingField.SHOW_DATES, index, isNew, 'value')
            });

            return validationResult;
        }

        const hasInvalidDates = value.some(date => !moment(date).isValid());

        if (hasInvalidDates) {
            validationResult.push({
                code: InternalGroupDefectCodes.INVALID_VALUE,
                params: null,
                path: errorPath(AdditionalTargetingField.SHOW_DATES, index, isNew, 'value')
            });
        }

        return validationResult;
    }
}
